import { PropertyGrid } from "./PropertyGrid";
import { EmptyState } from "./EmptyState";
import { PaginationControls } from "./PaginationControls";

interface Property {
  id: string;
  address: string;
  city: string;
  state: string;
  zip_code: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  square_feet: number;
  image_url: string | null;
  created_at: string;
  status: string;
  closed_date: string | null;
  contract_expiration_date: string | null;
  sale_price: number | null;
  seller_id: string;
}

interface ActiveDealsTabContentProps {
  properties: Property[];
  isLoading: boolean;
  searchTerm: string;
  daysOnSite: (createdAt: string) => number;
  tabContext: "all" | "active" | "pending" | "sold" | "expired" | "draft";
  currentPage: number;
  totalPages: number;
  totalCount: number;
  onPageChange: (page: number) => void;
}

export const ActiveDealsTabContent = ({ 
  properties, 
  isLoading, 
  searchTerm, 
  daysOnSite,
  tabContext,
  currentPage,
  totalPages,
  totalCount,
  onPageChange
}: ActiveDealsTabContentProps) => {
  if (isLoading) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Loading your deals...</p>
      </div>
    );
  }

  if (properties.length === 0) {
    const title = "No deals found";
    
    let description = "Try adjusting your search criteria";
    let showButton = false;
    
    if (!searchTerm) {
      showButton = true;
      switch (tabContext) {
        case "all":
          description = "You don't have any properties yet";
          break;
        case "active":
          description = "You don't have any active deals yet";
          break;
        case "pending":
          description = "You don't have any pending deals yet";
          break;
        case "sold":
          description = "You don't have any sold properties yet";
          showButton = false;
          break;
        case "expired":
          description = "You don't have any expired contracts yet";
          showButton = false;
          break;
        case "draft":
          description = "You don't have any saved drafts yet";
          break;
      }
    }
    
    return <EmptyState title={title} description={description} showButton={showButton} />;
  }

  const pageSize = properties.length;
  const startItem = currentPage * pageSize + 1;
  const endItem = Math.min(startItem + properties.length - 1, totalCount);

  return (
    <div>
      {totalCount > 0 && (
        <p className="text-sm text-muted-foreground mb-4">
          Showing {startItem}-{endItem} of {totalCount} properties
        </p>
      )}
      <PropertyGrid properties={properties} daysOnSite={daysOnSite} />
      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />
    </div>
  );
};
